<template>
  <v-container id="resource-list">
    <app-city-header />

    <v-row class="mt-4" align="center">
      <v-col cols="12" md="4">
        <v-select
          v-model="filters.status"
          :items="statusOptions"
          item-text="text"
          item-value="value"
          clearable
          label="Filtrar por status"
        />
      </v-col>
      <v-col cols="12" md="4">
        <v-text-field
          v-model="filters.search"
          label="Buscar por critério"
          clearable
        />
      </v-col>
      <v-col cols="12" md="4" class="text-right">
        <v-btn
          color="primary"
          class="ma-2"
          :to="newResourceRoute"
          v-if="canCreate"
        >
          Novo recurso
        </v-btn>
      </v-col>
    </v-row>

    <v-data-table
      :headers="headers"
      :items="filteredResources"
      :loading="loading"
      loading-text="Carregando recursos..."
      class="elevation-1"
      :no-data-text="loading ? 'Carregando...' : 'Nenhum recurso cadastrado'"
      :footer-props="{ itemsPerPageOptions: [10, 25, 50] }"
    >
      <template v-slot:item.status="{ item }">
        <resource-status-chip :status="item.status" />
      </template>
      <template v-slot:item.dataAtualizacao="{ item }">
        {{ formatDate(item.dataAtualizacao || item.dataCriacao) }}
      </template>
      <template v-slot:item.acoes="{ item }">
        <v-btn
          text
          small
          color="primary"
          :to="detailRoute(item.seloRecursoId)"
        >
          Detalhes
        </v-btn>
        <v-btn
          text
          small
          color="secondary"
          :to="editRoute(item.seloRecursoId)"
          v-if="canEdit(item)"
        >
          Editar
        </v-btn>
      </template>
    </v-data-table>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import { AppCityHeader } from "@/modules/city";
import { resourceAPI } from "../API/ResourceAPI";
import { extractCriterionDescription } from "@/helpers/criteria";
import { shouldTreatAsNational } from "@/utils/nationalEvaluation";
import {
  RESOURCE_STATUSES,
  findStatusMeta,
  normalizeResourceStatus,
} from "../constants";
import ResourceStatusChip from "./StatusChip.vue";

export default {
  name: "ResourceList",
  components: { AppCityHeader, ResourceStatusChip },
  data() {
    return {
      loading: false,
      resources: [],
      filters: {
        status: null,
        search: "",
      },
      headers: [
        { text: "Critério", value: "perguntaTitulo", sortable: true },
        { text: "Status", value: "status", sortable: true },
        {
          text: "Atualizado",
          value: "dataAtualizacao",
          sortable: true,
        },
        { text: "Solicitante", value: "responsavelNome", sortable: true },
        { text: "Ações", value: "acoes", sortable: false },
      ],
      config: JSON.parse(localStorage.getItem("config")) || {},
      groupsConfig: JSON.parse(localStorage.getItem("groupsConfig")) || {},
    };
  },
  computed: {
    ...mapGetters(["isFullAdmin", "cityId"]),
    municipioId() {
      return this.$route.params.municipioId || this.cityId;
    },
    statusOptions() {
      return RESOURCE_STATUSES.filter((status) => {
        if (status.value === "GESTAO_NACIONAL") {
          return false;
        }
        if (!this.canViewDrafts && status.value === "RASCUNHO") {
          return false;
        }
        return true;
      });
    },
    hasResourceAccess() {
      return (
        this.isFullAdmin ||
        ((this.config.recursos ?? false) && (this.groupsConfig.recursos ?? false))
      );
    },
    canManage() {
      return this.hasResourceAccess;
    },
    canViewDrafts() {
      if (this.isFullAdmin) {
        return true;
      }

      const configFlag = this.config.visualizarRascunhosRecursos;
      const groupFlag = this.groupsConfig.visualizarRascunhosRecursos;

      const configAllowed = configFlag === undefined ? true : Boolean(configFlag);
      const groupAllowed = groupFlag === undefined ? true : Boolean(groupFlag);

      return configAllowed && groupAllowed;
    },
    filteredResources() {
      const search = (this.filters.search || "").toLowerCase();

      return this.resources.filter((item) => {
        const matchesStatus =
          !this.filters.status || item.status === this.filters.status;

        const matchesSearch =
          !search ||
          (item.perguntaTitulo &&
            item.perguntaTitulo.toLowerCase().includes(search));

        const matchesDraftVisibility =
          this.canViewDrafts || item.status !== "RASCUNHO";

        const matchesTypePermission = this.hasResourceAccess
          ? true
          : this.canViewResource(item);

        return (
          matchesStatus &&
          matchesSearch &&
          matchesDraftVisibility &&
          matchesTypePermission
        );
      });
    },
    canCreate() {
      if (this.isFullAdmin) {
        return true;
      }

      const podeAcessarRecursos =
        (this.config.recursos ?? false) &&
        (this.groupsConfig.recursos ?? false);

      const podeCriarRecursos =
        (this.config.criarRecursos ?? false) &&
        (this.groupsConfig.criarRecursos ?? false);

      return podeAcessarRecursos && podeCriarRecursos;
    },
    newResourceRoute() {
      return `/cidade/${this.municipioId}/recursos/novo`;
    },
  },
  created() {
    this.load();
  },
  watch: {
    "$route.params.municipioId"() {
      this.load();
    },
  },
  methods: {
    normalizeBooleanFlag(value, defaultValue = false) {
      if (value === undefined || value === null) {
        return defaultValue;
      }

      if (value === true || value === 1) {
        return true;
      }

      if (value === false || value === 0) {
        return false;
      }

      if (typeof value === "string") {
        const normalized = value.trim().toLowerCase();
        if (["1", "true", "sim", "yes"].includes(normalized)) {
          return true;
        }
        if (["0", "false", "nao", "não", "no"].includes(normalized)) {
          return false;
        }
      }

      return Boolean(value);
    },
    normalizeClienteOcultoFlag(value) {
      return this.normalizeBooleanFlag(value, false);
    },
    canViewResource(item) {
      if (this.isFullAdmin) {
        return true;
      }

      const ehClienteOculto = this.normalizeClienteOcultoFlag(
        item?.ehPerguntaClienteOculto
      );
      const tratarComoNacional = shouldTreatAsNational(
        item?.perguntaId,
        ehClienteOculto
      );

      if (tratarComoNacional) {
        const groupNacional = this.normalizeBooleanFlag(
          this.groupsConfig?.avaliaRecursosNacionais,
          true
        );
        const configNacional = this.normalizeBooleanFlag(
          this.config?.avaliaRecursosNacionais,
          true
        );
        return groupNacional && configNacional;
      }

      const groupEstadual = this.normalizeBooleanFlag(
        this.groupsConfig?.avaliaRecursosEstaduais,
        true
      );
      const configEstadual = this.normalizeBooleanFlag(
        this.config?.avaliaRecursosEstaduais,
        true
      );
      return groupEstadual && configEstadual;
    },
    async load() {
      this.loading = true;
      try {
        const { data } = await resourceAPI.list(this.municipioId);
        const recursos = data.Recursos || data.recursos || [];
        this.resources = recursos.map((item) => this.normalize(item));
      } catch (error) {
        console.error("Erro ao carregar recursos", error);
        this.$toast?.error?.("Não foi possível carregar os recursos");
      } finally {
        this.loading = false;
      }
    },
    normalize(item) {
      const ehClienteOculto = this.normalizeClienteOcultoFlag(
        item.EhPerguntaClienteOculto ?? item.ehPerguntaClienteOculto ?? false
      );
      const ehRecursoNacional = shouldTreatAsNational(
        item.PerguntaId ?? item.perguntaId ?? null,
        ehClienteOculto
      );
      const perguntaId = item.PerguntaId ?? item.perguntaId ?? null;
      const perguntaTituloBase =
        item.PerguntaTitulo ??
        item.perguntaTitulo ??
        item.CriterioTitulo ??
        item.criterioTitulo ??
        item.ClienteOcultoPerguntaTitulo ??
        item.clienteOcultoPerguntaTitulo ??
        "";
      const grupoNomeBase =
        item.GrupoNome ??
        item.grupoNome ??
        item.ClienteOcultoGrupoNome ??
        item.clienteOcultoGrupoNome ??
        "";
      let perguntaTitulo = perguntaTituloBase
        ? perguntaTituloBase
        : ehClienteOculto && perguntaId
        ? `Cliente oculto - ${perguntaId}`
        : perguntaTituloBase;

      perguntaTitulo = extractCriterionDescription(perguntaTitulo || "");

      const rawStatus = item.Status ?? item.status ?? "";
      const status = normalizeResourceStatus(rawStatus);

      return {
        seloRecursoId: item.SeloRecursoId ?? item.seloRecursoId,
        municipioId: item.MunicipioId ?? item.municipioId,
        perguntaId: item.PerguntaId ?? item.perguntaId,
        perguntaTitulo,
        grupoNome: grupoNomeBase,
        notaOriginal: item.NotaOriginal ?? item.notaOriginal,
        notaPleiteada: item.NotaPleiteada ?? item.notaPleiteada,
        notaHomologada: item.NotaHomologada ?? item.notaHomologada ?? null,
        status,
        rawStatus,
        dataCriacao: item.DataCriacao ?? item.dataCriacao,
        dataAtualizacao: item.DataAtualizacao ?? item.dataAtualizacao,
        dataEnvio: item.DataEnvio ?? item.dataEnvio,
        dataConclusao: item.DataConclusao ?? item.dataConclusao,
        responsavelNome: item.ResponsavelNome ?? item.responsavelNome,
        ehPerguntaClienteOculto: ehClienteOculto,
        ehRecursoNacional,
      };
    },
    formatDate(value) {
      if (!value) return "—";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return value;
      }
      return date.toLocaleDateString("pt-BR");
    },
    detailRoute(id) {
      if (!id) {
        return "";
      }
      return `/cidade/${this.municipioId}/recursos/${id}`;
    },
    editRoute(id) {
      if (!id) {
        return "";
      }
      return `/cidade/${this.municipioId}/recursos/${id}/editar`;
    },
    canEdit(item) {
      const statusMeta = findStatusMeta(item.status);
      return (
        this.canManage &&
        ["RASCUNHO", "COMPLEMENTAR"].includes(statusMeta.value) &&
        (statusMeta.value !== "RASCUNHO" || this.canViewDrafts)
      );
    },
  },
};
</script>

<style scoped>
#resource-list {
  min-height: 80vh;
}
</style>

